import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getClosingListQueryKey,
  useClosingDelete,
} from "gen/endpoints/closing/closing";
import { getClosingTableRetrieveQueryKey } from "gen/endpoints/closing-table-data/closing-table-data";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import { investmentManagerDataRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { GeneralLedgerTypeEnum } from "gen/model/generalLedgerTypeEnum";
import { useTranslation } from "react-i18next";
import { PageMenu } from "shared/components/layout/PageMenu";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { downloadFileBlob, hyphensToUnderscores } from "shared/utils/utils";

interface ClosingData {
  closing_date: string;
  closing_id: string;
  fund_family_event_id: string;
  state: FundFamilyEventStateMachineFieldsEnum | undefined | null;
}

export interface ClosingMenuProps {
  fundFamilyId: string;
  closingData: ClosingData;
  onDiscard?: () => void;
}

export const useClosingMenuItems = ({
  fundFamilyId,
  closingData,
  onDiscard,
}: ClosingMenuProps) => {
  const { t } = useTranslation([
    "closing",
    "common",
    "enums",
    "capitalActivity",
  ]);
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  const { data: fundFamilyConfigData } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const hasWritePermission = useHasPermission("write");

  const { mutateAsync: deleteClosing } = useClosingDelete();

  const confirmDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("closing:discard"),
      description: t("closing:discardInformation"),
      successMessage: t("closing:discarded"),
      deleteButtonLabel: t("common:discardDraft"),
      performDelete: () =>
        deleteClosing({ fundFamilyId, closingId: closingData.closing_id }),
    },
  );

  const discardClosing = async () => {
    const success = await confirmDelete();
    if (success) {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: getClosingTableRetrieveQueryKey(fundFamilyId),
        }),
        queryClient.invalidateQueries({
          queryKey: getClosingListQueryKey(fundFamilyId, {
            is_draft: true,
          }),
        }),
      ]);
      onDiscard?.();
    }
  };

  const showYardiExport =
    fundFamilyConfigData.general_ledger_type == GeneralLedgerTypeEnum.yardi;

  const downloadYardiExport = async () => {
    const date = hyphensToUnderscores(closingData.closing_date);
    const filename = `yardi_export_closing_${date}.xlsx`;
    try {
      const excelExportBlob = await investmentManagerDataRetrieve(
        fundFamilyId,
        closingData.fund_family_event_id,
      );
      downloadFileBlob(excelExportBlob, filename);
    } catch (err) {
      showErrorToast(err);
    }
  };

  const menuItems = [];

  const isDraft =
    closingData.state === FundFamilyEventStateMachineFieldsEnum.draft;

  if (isDraft && hasWritePermission) {
    menuItems.push(
      <MenuItem key="discard" onClick={discardClosing} className="destructive">
        {t("common:discardDraft")}
      </MenuItem>,
    );
  }

  if (!isDraft && showYardiExport) {
    menuItems.push(
      <MenuItem key="yardi" onClick={downloadYardiExport}>
        {t("capitalActivity:thirdPartyExports.yardiDownload")}
      </MenuItem>,
    );
  }

  return menuItems;
};

export const ClosingPageMenu: React.FC<ClosingMenuProps> = (props) => {
  const menuItems = useClosingMenuItems(props);
  if (menuItems.length === 0) {
    return null;
  }
  return <PageMenu>{menuItems}</PageMenu>;
};
